import type { TargetKind } from './attention';

/**
 * SELECTION IS ATTENTION, AND ATTENTION IS FOLLOW.
 *
 * There is no Follow key any more. `F` and `shift-F` cycled the roster — press
 * a key, arrive at a stranger — and that is a global entity browser wearing a
 * camera's clothes: it hands a god somebody it has never met and never gone
 * looking for. **You cannot care about something you have never found.** So
 * discovery comes first and is spatial, always: fly, notice, point, choose.
 * Choosing is what moves the eye.
 *
 * What that leaves is this module, and since the orbit camera came back
 * (2026-08-17) it answers one question: **how close do you stand to watch
 * this?** A person is not a herd is not a valley. Frame the subject at the
 * scale at which its story makes sense — close enough to a deer to see what
 * it is doing, far enough from a herd to see it move, high enough over a
 * burnt hillside to see the shape of the burn. `main.ts` eases the orbit's
 * distance and elevation onto it when a thing is chosen, and the orbit's own
 * target holds the subject.
 *
 * The free flight's pose machinery — the drifting eye, the lateral offset for
 * the card, the eye settling behind a travelling subject's shoulder, the
 * envelope a god could wander inside before the eye let go — went with the
 * free flight. An orbit eye is where the hand put it; a subject is held by the
 * target and let go the moment the hand carries the target away.
 *
 * Pure arithmetic. No three, no DOM, no clock, no simulation.
 */

export interface Framing {
  /** Metres the eye stands off, measured on the ground plane. */
  standoff: number;
  /** Metres the eye rides above the subject's own ground. */
  height: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/**
 * How far off to stand, by what the subject IS.
 *
 * The numbers come from what each subject's story is made of. A person's is
 * what their hands are doing, which is a two-metre fact — but the framing has
 * to hold enough ground around them to see *what they are doing it to*, which
 * is why it is thirty metres and not five. A herd's story is movement, spread
 * and direction, none of which exist at one animal's scale. A landform's story
 * is its shape, and you cannot see the shape of a valley from inside it.
 *
 * `size` is the target's own footprint from `attention.ts`, so a great conifer
 * is framed further out than a sapling without either being a special case.
 */
export function framingFor(kind: TargetKind, size: number): Framing {
  switch (kind) {
    // a life: close, and still holding the ground it is standing on
    case 'person': return { standoff: 30, height: 14 };
    case 'animal': return { standoff: clamp(size * 12, 22, 60), height: clamp(size * 5, 10, 26) };
    // the group is the subject, not whichever body is nearest the middle
    case 'herd': return { standoff: 175, height: 88 };
    // a thing that grows or sits: it and its immediate ecology
    case 'plant': return { standoff: clamp(size * 4.5, 24, 95), height: clamp(size * 2.4, 13, 48) };
    case 'rock': return { standoff: clamp(size * 5, 24, 120), height: clamp(size * 2.6, 13, 60) };
    case 'litter': return { standoff: 14, height: 7 };
    // what people made, plus the place they made it in
    case 'structure': return { standoff: 48, height: 23 };
    case 'trail': return { standoff: 115, height: 58 };
    // your own stone, and enough land to see what its influence covers
    case 'anchor': return { standoff: 90, height: 42 };
    // a landform is a shape, and a shape needs the air over it
    case 'landmark': return { standoff: clamp(size * 1.5, 420, 2600),
                              height: clamp(size * 0.85, 240, 1500) };
    default: return { standoff: 260, height: 150 };
  }
}

/**
 * Does watching this mean travelling with it?
 *
 * Everything else settles into a hover. This is not a list of "followable
 * types" in the compatibility-table sense law 13 warns about — it is a
 * statement about which subjects have a position that changes, which is a fact
 * about the world rather than a permission.
 */
export const travels = (kind: TargetKind): boolean =>
  kind === 'person' || kind === 'animal' || kind === 'herd';
